import { useEffect } from "react";
import { useLocation } from "wouter";
import { SEO_CANONICAL_ORIGIN, SEO_SITE_NAME } from "@shared/seo";
import { resolveHeadMeta } from "@/ssr/prefetch";

function setMeta(selector: string, attributes: Record<string, string>) {
  let node = document.head.querySelector<HTMLMetaElement>(selector);
  if (!node) {
    node = document.createElement("meta");
    document.head.appendChild(node);
  }
  Object.entries(attributes).forEach(([key, value]) => node?.setAttribute(key, value));
}

function setCanonical(href: string) {
  let node = document.head.querySelector<HTMLLinkElement>('link[rel="canonical"]');
  if (!node) {
    node = document.createElement("link");
    node.rel = "canonical";
    document.head.appendChild(node);
  }
  node.href = href;
}

export function Head() {
  const [location] = useLocation();
  useEffect(() => {
    const head = resolveHeadMeta(location);
    const canonical = `${SEO_CANONICAL_ORIGIN}${head.canonicalPath || location}`;
    const image = head.ogImage ? `${SEO_CANONICAL_ORIGIN}${head.ogImage}` : "";
    document.title = head.title;
    setMeta('meta[name="description"]', { name: "description", content: head.description });
    setMeta('meta[property="og:type"]', { property: "og:type", content: head.ogType || "website" });
    setMeta('meta[property="og:title"]', { property: "og:title", content: head.title });
    setMeta('meta[property="og:description"]', { property: "og:description", content: head.description });
    setMeta('meta[property="og:url"]', { property: "og:url", content: canonical });
    setMeta('meta[name="twitter:title"]', { name: "twitter:title", content: head.title });
    setMeta('meta[name="twitter:description"]', { name: "twitter:description", content: head.description });
    setMeta('meta[name="twitter:card"]', { name: "twitter:card", content: image ? "summary_large_image" : "summary" });
    if (image) {
      setMeta('meta[property="og:image"]', { property: "og:image", content: image });
      setMeta('meta[name="twitter:image"]', { name: "twitter:image", content: image });
    }
    setMeta('meta[name="robots"]', { name: "robots", content: head.noindex || head.notFound ? "noindex, follow" : "index, follow" });
    setCanonical(canonical);
  }, [location]);
  return null;
}

export function useDocumentTitle(title: string | undefined) {
  useEffect(() => {
    document.title = title?.trim() ? `${title} | ${SEO_SITE_NAME}` : SEO_SITE_NAME;
  }, [title]);
}
